import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  audio: {
    music: {
      enabled: true,
      volume: 0.6,
    },
    sounds: {
      enabled: true,
      volume: 0.8,
    },
  },

  sidebar: {
    isOpen: false,
    history: [],
  },

  // Live status of the embedded game canvas (iframe). Driven by the canvas
  // bridge from the canvas' LOADING/READY/ERROR messages. The boot loader gates
  // its handoff on `ready` (or `error`) and blends `progress` into its bar.
  canvas: {
    ready: false,
    progress: 0, // 0..1
    error: false,
  },

  // Embedded chat iframe. `open` drives the mobile overlay (desktop uses the
  // activity-panel tab); `ready`/`connected` are set once the chat completes its
  // handshake through the communication bridge.
  chat: {
    open: false,
    ready: false,
    connected: false,
  },

  // Buy Bonus purchase modal — mounted once via OverlayController, revealed by
  // `open` (triggered from the betting panel header button).
  buyBonus: {
    open: false,
  },
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,

  reducers: {
    openSidebar(state) {
      state.sidebar.isOpen = true
      state.sidebar.history = []
    },

    closeSidebar(state) {
      state.sidebar.isOpen = false
      state.sidebar.history = []
    },

    openScreen(state, action) {
      state.sidebar.history.push(action.payload)
    },

    goBack(state) {
      if (state.sidebar.history.length > 0) {
        state.sidebar.history.pop()
      }
    },

    // Patches `params` on the CURRENT top-of-stack screen (e.g. a list screen
    // remembering its active tab across a drill-down + back, without a
    // feature-specific slice). No-op if the sidebar is closed.
    updateScreenParams(state, action) {
      const current = state.sidebar.history.at(-1)
      if (!current) return
      current.params = { ...current.params, ...action.payload }
    },

    toggleMusic(state) {
      state.audio.music.enabled = !state.audio.music.enabled
    },

    toggleSound(state) {
      state.audio.sounds.enabled = !state.audio.sounds.enabled
    },

    setMusicVolume(state, action) {
      state.audio.music.volume = action.payload
    },

    setSoundVolume(state, action) {
      state.audio.sounds.volume = action.payload
    },

    setCanvasProgress(state, action) {
      state.canvas.progress = action.payload
    },

    setCanvasReady(state, action) {
      state.canvas.ready = action.payload ?? true
    },

    setCanvasError(state, action) {
      state.canvas.error = action.payload ?? true
    },

    resetCanvas(state) {
      state.canvas = { ready: false, progress: 0, error: false }
    },

    openChat(state) {
      state.chat.open = true
    },

    closeChat(state) {
      state.chat.open = false
    },

    toggleChat(state) {
      state.chat.open = !state.chat.open
    },

    setChatReady(state, action) {
      state.chat.ready = action.payload ?? true
    },

    setChatConnected(state, action) {
      state.chat.connected = action.payload ?? true
    },

    resetChat(state) {
      state.chat = { open: false, ready: false, connected: false }
    },

    openBuyBonus(state) {
      state.buyBonus.open = true
    },

    closeBuyBonus(state) {
      state.buyBonus.open = false
    },
  },
})

export const {
  openSidebar,
  closeSidebar,
  openScreen,
  goBack,
  updateScreenParams,
  toggleMusic,
  toggleSound,
  setMusicVolume,
  setSoundVolume,
  setCanvasProgress,
  setCanvasReady,
  setCanvasError,
  resetCanvas,
  openChat,
  closeChat,
  toggleChat,
  setChatReady,
  setChatConnected,
  resetChat,
  openBuyBonus,
  closeBuyBonus,
} = uiSlice.actions

export default uiSlice.reducer
